import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon, InfinityIcon, TruckIcon, RotateCcwIcon, ShieldCheckIcon } from 'lucide-react';
import { getLegalDoc, legalDocs, LegalSection } from '../data/legal';
import { media } from '../data/product';
import { SiteFooter } from '../components/SiteFooter';
import { PaymentLogos } from '../components/PaymentLogos';
const promises = [{
  icon: InfinityIcon,
  label: 'Break it. We replace it.'
}, {
  icon: RotateCcwIcon,
  label: '30-day returns, we pay shipping'
}, {
  icon: TruckIcon,
  label: 'Ships from the U.S. in 1 business day'
}, {
  icon: ShieldCheckIcon,
  label: 'Real person on support'
}];
export function Legal() {
  const {
    slug = ''
  } = useParams();
  const doc = getLegalDoc(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const renderSection = (section: LegalSection) => <section key={section.heading} className="border-t border-white/10 pt-7" data-id="element-1306" data-mp="3s605tcibykrw">
      <h2 className={section.fine ? 'font-display text-lg font-bold uppercase tracking-wide text-steel-400' : 'font-display text-2xl font-extrabold uppercase leading-tight text-offwhite sm:text-3xl'} data-id="element-1307" data-mp="27kd54pdvo948">
        {section.heading}
      </h2>
      <div className={`mt-3 grid max-w-3xl ${section.fine ? 'gap-2' : 'gap-3'}`} data-id="element-1308" data-mp="2cjy5934sanij">
        {section.body.map(paragraph => <p key={paragraph} className={section.fine ? 'text-[12px] leading-relaxed text-steel-500' : 'text-[15px] leading-relaxed text-steel-300 sm:text-base'} data-id="element-1309" data-mp="1ov6joulp93eq">
            {paragraph}
          </p>)}
      </div>
    </section>;
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1310" data-mp="1645mab089fxa">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink-950/95 backdrop-blur" data-id="element-1311" data-mp="1622lqx1g84eb">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8" data-id="element-1312" data-mp="112ydq9q9gcpk">
          <Link to="/" className="shrink-0" data-id="element-1313" data-mp="366exu32t9qic">
            <span className="block font-display text-2xl font-extrabold uppercase leading-none tracking-tight text-offwhite sm:text-3xl" data-id="element-1314" data-mp="365dd16vztpnf">
              Grit<span className="text-grit-500" data-id="element-1315" data-mp="3azhitmtivrmx">Vue</span>
              <sup className="ml-0.5 align-super text-[9px] text-steel-400" data-id="element-1316" data-mp="3exno0qeppz5q">™</sup>
            </span>
            <span className="mt-0.5 block text-[8px] font-semibold uppercase tracking-[0.3em] text-steel-400 sm:text-[9px]" data-id="element-1317" data-mp="2i9rdo39vc02y">
              See the job through
            </span>
          </Link>
          <Link to="/#offer" className="inline-flex shrink-0 items-center bg-grit-500 px-3.5 py-2.5 font-display text-base font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:px-4 sm:text-lg" data-id="element-1318" data-mp="2r82ytpryu5o0">
            Get your X1
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8 lg:py-16" data-id="element-1319" data-mp="llf5wm6odevh">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1320" data-mp="go56cdpknmtx">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1321" data-mp="7u9z904w3mbn" />
          Back to the X1
        </Link>

        {!doc ? <div className="mt-8" data-id="element-1322" data-mp="2t2tt7q7yz85">
            <h1 className="grit-headline text-[clamp(2.25rem,8vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-1323" data-mp="3u1gxcndkjll3">
              Page not found
            </h1>
            <p className="mt-4 text-lg text-steel-300" data-id="element-1324" data-mp="3p4gqwbct4gtg">Pick one of our policies below.</p>
          </div> : <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16" data-id="element-1325" data-mp="1nb0oyib9rfj6">
            <article data-id="element-1326" data-mp="1n8xyxya49meg">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-grit-500" data-id="element-1327" data-mp="1j9ypq1hcr3h7">{doc.eyebrow}</p>
              <h1 className="grit-headline mt-3 text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase text-offwhite" data-id="element-1328" data-mp="1qcdhzuxooe6o">
                {doc.title}
              </h1>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-steel-500" data-id="element-1329" data-mp="1v9m2412e6sak">
                Last updated {doc.updated}
              </p>
              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-steel-200" data-id="element-1330" data-mp="1lchdzvozkmr4">{doc.intro}</p>

              <div className="mt-10 grid gap-8" data-id="element-1331" data-mp="17lm5wlmba9ol">
                {doc.sections.filter(section => !section.fine).map(renderSection)}
              </div>

              <div className="mt-12 grid gap-px border-2 border-grit-500 bg-white/10 lg:grid-cols-[1.1fr_1fr]" data-id="element-1332" data-mp="xohqd4mz1cgr">
                <img src={media.lensSheet} alt="GritVue X1 illuminated work glasses shown with its clear, amber and smoke lenses" className="block h-auto w-full bg-ink-950 object-contain" data-id="element-1333" data-mp="12mrnvlw64p5j" />
                <div className="flex flex-col justify-center bg-ink-950 p-6 lg:p-8" data-id="element-1334" data-mp="topcccet6e40">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-grit-500" data-id="element-1335" data-mp="a12zcbahrv2g">
                    Complete system from $49.95
                  </p>
                  <p className="grit-headline mt-3 text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold uppercase text-offwhite" data-id="element-1336" data-mp="j2kcplwiling">
                    Still want yours?
                    <br data-id="element-1337" data-mp="szoz9mu9vg4s" />
                    <span className="text-grit-500" data-id="element-1338" data-mp="syx58ggbpqgm">Pick your pack.</span>
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-1339" data-mp="o1dtpob0frsd">
                    Every X1 ships as the complete system: 3 lenses, hard case, care kit, USB cable, anti-fog spray and
                    the Lifetime Replacement Warranty free on every multi-pack.
                  </p>
                  <Link to="/#offer" className="mt-6 inline-flex items-center justify-center gap-2 bg-grit-500 px-6 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:text-2xl" data-id="element-1340" data-mp="1bpeyqn8zcq31">
                    Back to the packs
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1341" data-mp="dcsu6py60ila" />
                  </Link>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-steel-400" data-id="element-1342" data-mp="i6n8fdv045cz">
                    Buy 2, get 1 free: $99.90 · $33.30 per X1 · Free U.S. shipping
                  </p>
                </div>
              </div>

              {doc.sections.some(section => section.fine) && <div className="mt-10 grid gap-8" data-id="element-1343" data-mp="x34nog6ba44t">
                  {doc.sections.filter(section => section.fine).map(renderSection)}
                </div>}

              <div className="mt-8 flex flex-col gap-4 border border-white/12 bg-ink-900 p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1344" data-mp="2yv15g7j2oa81">
                <p data-id="element-1345" data-mp="32x4hax9nlgtu">
                  <span className="block font-display text-2xl font-extrabold uppercase leading-none text-grit-500" data-id="element-1346" data-mp="2vcbtliz1xh5n">
                    Still have a question?
                  </span>
                  <a href="mailto:support@gritvuegear.com" className="mt-2 inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-wide text-offwhite transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1347" data-mp="2llz1n5owqlph">
                    <MailIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-1348" data-mp="3e8aa12ir9qwu" />
                    support@gritvuegear.com
                  </a>
                </p>
                <Link to="/#offer" className="inline-flex items-center justify-center bg-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px]" data-id="element-1349" data-mp="358euymm2xbrj">
                  Shop the X1
                </Link>
              </div>
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1350" data-mp="3szkun9y4ml0o">
              <nav aria-label="Policies" className="border border-white/12 bg-ink-900" data-id="element-1351" data-mp="3t0vzld3b5ytd">
                <p className="border-b border-white/10 px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1352" data-mp="3f1cruleesphl">
                  Policies
                </p>
                <ul data-id="element-1353" data-mp="bk4b64vf3vog">
                  {legalDocs.map(item => <li key={item.slug} className="border-b border-white/5 last:border-b-0" data-id="element-1354" data-mp="ghwfxzh5vca2">
                      <Link to={`/legal/${item.slug}`} className={`block px-5 py-3.5 font-display text-lg font-bold uppercase tracking-wide transition-colors duration-150 ease-grit ${item.slug === doc.slug ? 'bg-grit-500/10 text-grit-500' : 'text-steel-300 hover:text-offwhite'}`} data-id="element-1355" data-mp="1t9oize9eu2h">
                        {item.title}
                      </Link>
                    </li>)}
                </ul>
              </nav>

              <ul className="mt-6 grid gap-3 border border-white/12 bg-ink-900 p-5" data-id="element-1356" data-mp="2sr4lyficdspb">
                {promises.map(promise => {
              const Icon = promise.icon;
              return <li key={promise.label} className="flex items-start gap-3 text-[14px] font-medium text-steel-200" data-id="element-1357" data-mp="20zot43q6z9qn">
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1358" data-mp="1w25ev3thuwyp" />
                      {promise.label}
                    </li>;
            })}
              </ul>

              <div className="mt-6" data-id="element-1359" data-mp="2508q97armdcs">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-steel-500" data-id="element-1360" data-mp="3lg1uvs0nyd5e">Secure checkout</p>
                <div className="mt-2" data-id="element-1361" data-mp="3gh7ailn1sgj6">
                  <PaymentLogos data-id="element-1362" data-mp="3ggfh9hgxauw2" />
                </div>
              </div>
            </aside>
          </div>}
      </main>

      <SiteFooter data-id="element-1363" data-mp="17c21nf2ppv1b" />
    </div>;
}